<template>
  <div>
    <v-select
      v-if="showRegions"
      v-model="selectedRegions"
      :items="regions"
      item-text="text"
      item-value="value"
      label="Regions"
      clearable
      small-chips
      deletable-chips
      return-object
      multiple
      :disabled="readOnly"
    >
      <template v-slot:prepend-item>
        <v-list-item ripple @mousedown.prevent @click="selectAllRegions">
          <v-list-item-action>
            <v-icon :color="selectedRegions.length > 0 ? 'primary' : ''">
              {{ regionsAllIcon }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              Select All
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
      </template>
    </v-select>

    <v-autocomplete
      v-if="showCountries"
      v-model="selectedCountries"
      :items="countriesAll"
      item-text="text"
      item-value="value"
      label="Countries"
      clearable
      small-chips
      return-object
      multiple
      :disabled="readOnly"
    >
      <template v-slot:prepend-item>
        <v-list-item v-if="selectedRegions.length" class="mt-2">
          <v-list-item-content>
            <v-list-item-title>
              <span class="grey--text"
                >All countries within currently selected regions:</span
              ><br />
              {{ selectedRegionsText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-2" v-if="selectedRegions.length"></v-divider>
      </template>
      <template v-slot:selection="{ index }">
        <span v-if="index === 0">
          {{ selectedCountries.length }} countries selected
        </span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="showCities"
      v-model="selectedCities"
      :items="citiesAll"
      item-text="text"
      item-value="value"
      label="Cities"
      clearable
      small-chips
      return-object
      multiple
      :disabled="readOnly"
    >
      <template v-slot:selection="{ index }">
        <span v-if="index === 0">
          {{ selectedCities.length }} cities selected
        </span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="showCounties"
      v-model="selectedCounties"
      :items="countiesAll"
      item-text="text"
      item-value="value"
      label="US Counties"
      clearable
      small-chips
      return-object
      multiple
      :disabled="readOnly"
    >
      <template v-slot:selection="{ index }">
        <span v-if="index === 0">
          {{ selectedCounties.length }} counties selected
        </span>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";

import {
  getAllCountries,
  getAllRegions,
  getAllCities,
  getAllCounties,
} from "@/common/maps/utilities";

const EMITTED_ACTIONS = {
  CHANGE_LOCATIONS: "change-locations",
};

export default {
  name: "LocationsSelector",
  components: {},
  props: {
    selectedLocations: {
      type: Object,
    },
    showRegions: {
      type: Boolean,
      default: true,
    },
    showCountries: {
      type: Boolean,
      default: true,
    },
    showCities: {
      type: Boolean,
      default: false,
    },
    showCounties: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      regions: [],
      countries: [],
      countriesAll: [],
      cities: [],
      citiesAll: [],
      counties: [],
      countiesAll: [],
      selectedRegions: lodashGet(this.selectedLocations, "regions") || [],
      selectedCountries: lodashGet(this.selectedLocations, "countries") || [],
      selectedCities: lodashGet(this.selectedLocations, "cities") || [],
      selectedCounties: lodashGet(this.selectedLocations, "counties") || [],
    };
  },
  computed: {
    selectedRegionsText() {
      if (this.selectedRegions.length) {
        const regionsText = this.selectedRegions.map((region) => region.text);
        return regionsText.join(", ");
      }
      return null;
    },
    regionsSelectedAll() {
      return this.selectedRegions.length === this.regions.length;
    },
    regionsSelectedSome() {
      return this.selectedRegions.length > 0 && !this.regionsSelectedAll;
    },
    regionsAllIcon() {
      if (this.regionsSelectedAll) {
        return "mdi-close-box";
      }
      if (this.regionsSelectedSome) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    countriesSelectedAll() {
      return this.selectedCountries.length === this.countries.length;
    },
    countriesSelectedSome() {
      return this.selectedCountries.length > 0 && !this.countriesSelectedAll;
    },
    countriesAllIcon() {
      if (this.countriesSelectedAll) {
        return "mdi-close-box";
      }
      if (this.countriesSelectedSome) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    citiesSelectedAll() {
      return this.selectedCities.length === this.cities.length;
    },
    citiesSelectedSome() {
      return this.selectedCities.length > 0 && !this.citiesSelectedAll;
    },
    citiesAllIcon() {
      if (this.citiesSelectedAll) {
        return "mdi-close-box";
      }
      if (this.citiesSelectedSome) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    countiesSelectedAll() {
      return this.selectedCounties.length === this.counties.length;
    },
    countiesSelectedSome() {
      return this.selectedCounties.length > 0 && !this.countiesSelectedAll;
    },
    countiesAllIcon() {
      if (this.countiesSelectedAll) {
        return "mdi-close-box";
      }
      if (this.countiesSelectedSome) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
  },
  async mounted() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      this.regions =
        (await getAllRegions())?.map((r) => ({
          value: r.properties.code,
          text: r.properties.name,
          countries: r.properties.countries.map((c) => ({
            value: c.isoAlpha2Code,
            text: c.name,
          })),
        })) || [];
      this.regions = lodashOrderBy(this.regions, ["text"]);

      this.countriesAll =
        (await getAllCountries())?.map((r) => ({
          value: r.properties.iso2,
          text: r.properties.displayName,
        })) || [];
      this.countriesAll = this.countries = lodashOrderBy(this.countriesAll, [
        "text",
      ]);

      this.citiesAll =
        (await getAllCities())?.map((r) => ({
          value: r.properties.code,
          text: `${r.properties.displayName}, ${r.properties.countryName}`,
        })) || [];
      this.citiesAll = this.cities = lodashOrderBy(this.citiesAll, ["text"]);

      this.countiesAll =
        (await getAllCounties())?.map((r) => ({
          value: r.properties.code,
          text: `${r.properties.displayName}, ${r.properties.state}`,
        })) || [];
      this.countiesAll = this.counties = lodashOrderBy(this.countiesAll, [
        "text",
      ]);
    },
    selectAllRegions() {
      this.$nextTick(() => {
        if (this.selectedRegions.length === this.regions.length) {
          this.selectedRegions = [];
        } else {
          this.selectedRegions = this.regions.slice();
        }
        this.locationsChanged();
      });
    },
    selectAllCountries() {
      this.$nextTick(() => {
        if (this.selectedCountries.length === this.countries.length) {
          this.selectedCountries = [];
        } else {
          this.selectedCountries = this.countries.slice();
        }
        this.locationsChanged();
      });
    },
    selectAllCities() {
      this.$nextTick(() => {
        if (this.selectedCities.length === this.cities.length) {
          this.selectedCities = [];
        } else {
          this.selectedCities = this.cities.slice();
        }
        this.locationsChanged();
      });
    },
    selectAllCounties() {
      this.$nextTick(() => {
        if (this.selectedCounties.length === this.counties.length) {
          this.selectedCounties = [];
        } else {
          this.selectedCounties = this.counties.slice();
        }
        this.locationsChanged();
      });
    },
    locationsChanged() {
      const locations = {};
      if (this.showRegions) {
        locations.regions = this.selectedRegions;
      }
      if (this.showCountries) {
        locations.countries = this.selectedCountries;
      }
      if (this.showCities) {
        locations.cities = this.selectedCities;
      }
      if (this.showCounties) {
        locations.counties = this.selectedCounties;
      }
      this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, locations);
    },
  },
  watch: {
    selectedRegions: {
      handler() {
        this.locationsChanged();
      },
      deep: true,
    },
    selectedCountries: {
      handler() {
        this.locationsChanged();
      },
      deep: true,
    },
    selectedCities: {
      handler() {
        this.locationsChanged();
      },
      deep: true,
    },
    selectedCounties: {
      handler() {
        this.locationsChanged();
      },
      deep: true,
    },
  },
};
</script>

<style scoped></style>
